import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getOptimizedProfilePhoto } from '../../services/userService';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getSubscription, checkPremiumAccess, createPortalSession } from '../../services/stripeService';
import { getGeneralSettings } from '../../services/systemSettingsService';

const useAccountSettings = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [systemVersion, setSystemVersion] = useState('');
  const [planDetails, setPlanDetails] = useState({
    planName: 'Free',
    planType: 'hobby',
    subscriptionStatus: 'none',
    hasPremiumAccess: false,
    nextPaymentDate: null,
    billingCycle: '',
    showUpgradeOption: true
  });
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const navigate = useNavigate();

  // Process subscription data to determine plan details
  const processPlanDetails = (subscriptionData, hasPremium) => {
    let currentPlanName = 'Free';
    let currentPlanType = 'hobby';
    let currentStatus = 'none'; // Default status
    let billingCycle = '';
    let nextPayment = null;
    let showUpgrade = true;

    if (subscriptionData) {
      currentStatus = subscriptionData.status || 'unknown'; // Get status from subscription
      // Debug log for subscription
      console.log('[AccountSettings] subscriptionData:', subscriptionData);

      // Use subscription.product as the plan name/type
      let productRaw = subscriptionData.product;
      let productStr = typeof productRaw === 'string' ? productRaw : (productRaw?.id || '');
      let productLower = productStr.toLowerCase();

      if (productLower.includes('business')) {
        currentPlanName = 'Business';
        currentPlanType = 'business';
        showUpgrade = currentStatus === 'active' || currentStatus === 'trialing';
      } else if (productLower.includes('pro')) {
        currentPlanName = 'Pro';
        currentPlanType = 'pro';
        showUpgrade = currentStatus === 'active' || currentStatus === 'trialing';
      } else if (productLower) {
        currentPlanName = productStr;
        currentPlanType = 'custom';
        showUpgrade = false;
      } else {
        currentPlanName = 'Free';
        currentPlanType = 'hobby';
        showUpgrade = true;
      }

      // Only set billing info if subscription is active or trialing
      if (currentStatus === 'active' || currentStatus === 'trialing') {
        if (subscriptionData.items && subscriptionData.items.length > 0) {
          const price = subscriptionData.items[0].price; // price object
          if (price && price.recurring) {
            billingCycle = price.recurring.interval === 'year' ? 'Yearly' : 'Monthly';
          }
        }
        if (subscriptionData.current_period_end) {
          nextPayment = new Date(subscriptionData.current_period_end.seconds * 1000);
        }
      }
    }

    return {
      planName: currentPlanName,
      planType: currentPlanType,
      subscriptionStatus: currentStatus,
      hasPremiumAccess: hasPremium,
      nextPaymentDate: nextPayment,
      billingCycle: billingCycle,
      showUpgradeOption: showUpgrade
    };
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('No user is signed in');
      }
      
      // Start subscription fetch early (in parallel)
      const subscriptionPromise = getSubscription();
      const premiumAccessPromise = checkPremiumAccess();
      const generalSettingsPromise = getGeneralSettings();
      
      // Get the optimized photo URL
      const photoURL = getOptimizedProfilePhoto(user);
      
      // Get additional user data from Firestore
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const firestoreData = userDoc.exists() ? userDoc.data() : {};

      // Wait for subscription data, premium access check, and general settings to complete
      const [subscriptionData, hasPremium, generalSettings] = await Promise.all([
        subscriptionPromise,
        premiumAccessPromise,
        generalSettingsPromise
      ]);
      
      setSubscription(subscriptionData);
      setSystemVersion(generalSettings.systemVersion);
      
      // Process plan details
      const details = processPlanDetails(subscriptionData, hasPremium);
      setPlanDetails(details);
        
        // Set user data
        setUserData({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
        phoneNumber: firestoreData.phoneNumber || '',
          photoURL: photoURL,
          ...firestoreData
        });
        
        setError(null);
      } catch (err) {
        setError('Failed to load user data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Updated handler to open Stripe Customer Portal
  const handleSubscriptionManage = async () => {
    setIsPortalLoading(true);
    setPortalError('');
    
    try {
      // Use the updated createPortalSession function
      const { url } = await createPortalSession(window.location.origin + '/account-settings');
      
      // Redirect to Stripe portal
      window.location.href = url;
    } catch (error) {
      console.error('Error opening customer portal:', error);
      // Set error message for display
      if (error.message && error.message.includes("No such customer")) {
        setPortalError('No matching customer found in Stripe. Try resetting your customer data or contact support.');
      } else if (error.message && error.message.includes("Could not find the createPortalLink")) {
        setPortalError('Error: Cloud function for portal not found. Verify the extension is properly installed.');
      } else if (error.message && error.message.includes("Permission denied")) {
        setPortalError('Error: No permission to call the cloud function. Make sure you are logged in.');
      } else {
        setPortalError('An error occurred while opening the customer portal. Please try again later.');
      }
    } finally {
      setIsPortalLoading(false);
    }
  };

  const handleUpgradeToBusiness = () => {
    navigate('/pricing?plan=business');
  };

  const toggleAdvanced = () => {
    setIsAdvancedOpen(!isAdvancedOpen);
  };

  const handleOpenProfileEdit = () => {
    setShowProfileEdit(true);
  };

  const handleCloseProfileEdit = () => {
    setShowProfileEdit(false);
  };

  const handleProfileSuccess = (updatedData) => {
    setUserData(prevData => ({
      ...prevData,
      ...updatedData
    }));
  };

  return {
    userData,
    loading,
    error,
    isAdvancedOpen,
    subscription,
    planDetails,
    systemVersion,
    isPortalLoading,
    portalError,
    showProfileEdit,
    handleSubscriptionManage,
    handleUpgradeToBusiness,
    toggleAdvanced,
    handleOpenProfileEdit,
    handleCloseProfileEdit,
    handleProfileSuccess,
    refreshUserData: fetchUserData
  };
};

export default useAccountSettings; 